/**
 * Phone + SMS preference API service.
 *
 * Phone updates go through PATCH /api/users/me/phone/. SMS opt-in toggles
 * go through POST /api/users/<id>/sms-consent/ so every consent change
 * is recorded in the ConsentRecord audit trail (Twilio A2P 10DLC).
 */

import { getApiUrl } from '../config/api';
import { safeJsonParse } from '../utils/apiUtils';

export interface PhoneSmsState {
  phone: string;
  sms_opt_in: boolean;
}

export const updatePhone = async (digits: string): Promise<PhoneSmsState> => {
  const token = localStorage.getItem('access');
  if (!token) throw new Error('No authentication token found');

  const response = await fetch(getApiUrl('/api/users/me/phone/'), {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phone: digits }),
  });

  const result = await safeJsonParse<PhoneSmsState | Record<string, string>>(
    response,
    'updating phone',
  );

  if (!result.success || !response.ok) {
    if (response.status === 401) throw new Error('Authentication failed. Please log in again.');
    if (response.status === 400 && result.data) {
      const data = result.data as Record<string, string>;
      throw new Error(data.phone || 'Invalid phone number.');
    }
    throw new Error(result.error || `Failed to update phone: ${response.statusText}`);
  }

  return result.data as PhoneSmsState;
};

export const recordSmsConsent = async (
  userId: number,
  action: 'granted' | 'revoked',
  phrasing: string,
): Promise<void> => {
  const token = localStorage.getItem('access');
  if (!token) throw new Error('No authentication token found');

  const response = await fetch(getApiUrl(`/api/users/${userId}/sms-consent/`), {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      action,
      source: 'user_settings_update',
      phrasing_shown: phrasing,
    }),
  });

  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed. Please log in again.');
    const body = await response.text();
    throw new Error(body || `Failed to record SMS consent: ${response.statusText}`);
  }
};
